import { formatCurrency } from "../../lib/formatters.js";

function parseCurrency(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function impliedOngoingState(row) {
  return row.impliedOngoing > row.ongoingBudget ? "bad" : "";
}

function ongoingDeltaState(row) {
  if (row.ongoingDelta > 0) {
    return "bad";
  }

  if (row.ongoingDelta < 0) {
    return "good";
  }

  return "";
}

function irregularDeltaState(row) {
  if (row.irregularDelta > 0) {
    return "bad";
  }

  if (row.irregularDelta < 0) {
    return "good";
  }

  return "";
}

const budgetGridColumnsBasic = [
  {
    key: "category",
    label: "Category",
    type: "text",
    editable: true,
    align: "text",
    inputType: "text",
    getDraftValue: (row) => String(row.category ?? ""),
    parseDraft: (value) => value.trim(),
    displayValue: (row) => row.category
  },
  {
    key: "ongoingBudget",
    label: "Ongoing Budget",
    type: "currency",
    editable: true,
    align: "number",
    inputType: "number",
    step: "0.01",
    min: "0",
    getDraftValue: (row) => String(row.ongoingBudget ?? 0),
    parseDraft: parseCurrency,
    displayValue: (row) => formatCurrency(row.ongoingBudget)
  },
  {
    key: "irregularBudget",
    label: "Irregular Budget",
    type: "currency",
    editable: true,
    align: "number",
    inputType: "number",
    step: "0.01",
    min: "0",
    getDraftValue: (row) => String(row.irregularBudget ?? 0),
    parseDraft: parseCurrency,
    displayValue: (row) => formatCurrency(row.irregularBudget)
  }
];

export function createBudgetGridColumns(maxAbsDelta, t = (key) => key) {
  return [
  {
    key: "category",
    label: t("category"),
    type: "text",
    editable: true,
    align: "text",
    inputType: "text",
    getDraftValue: (row) => String(row.category ?? ""),
    parseDraft: (value) => value.trim(),
    displayValue: (row) => row.category
  },
  {
    key: "ongoingBudget",
    label: t("ongoingBudget"),
    type: "currency",
    editable: true,
    align: "number",
    inputType: "number",
    step: "0.01",
    min: "0",
    getDraftValue: (row) => String(row.ongoingBudget ?? 0),
    parseDraft: parseCurrency,
    displayValue: (row) => formatCurrency(row.ongoingBudget)
  },
  {
    key: "impliedOngoing",
    label: t("impliedOngoing"),
    type: "currency",
    editable: false,
    align: "number",
    cellState: impliedOngoingState,
    displayValue: (row) => formatCurrency(row.impliedOngoing)
  },
  {
    key: "ongoingDelta",
    label: t("variance"),
    type: "currency",
    editable: false,
    align: "number",
    cellState: ongoingDeltaState,
    sortValue: (row) => row.ongoingDelta,
    displayValue: (row) => formatCurrency(row.ongoingDelta)
  },
  {
    key: "irregularBudget",
    label: t("irregularBudget"),
    type: "currency",
    editable: true,
    align: "number",
    inputType: "number",
    step: "0.01",
    min: "0",
    getDraftValue: (row) => String(row.irregularBudget ?? 0),
    parseDraft: parseCurrency,
    displayValue: (row) => formatCurrency(row.irregularBudget)
  },
  {
    key: "irregularSpent",
    label: t("irregularSpent"),
    type: "currency",
    editable: false,
    align: "number",
    displayValue: (row) => formatCurrency(row.irregularSpent)
  },
  {
    key: "irregularDelta",
    label: t("variance"),
    type: "currency",
    editable: false,
    align: "number",
    cellState: irregularDeltaState,
    sortValue: (row) => row.irregularDelta,
    displayValue: (row) => formatCurrency(row.irregularDelta)
  }
  ];
}

export function createBudgetGridColumnsBasic(t = (key) => key) {
  return budgetGridColumnsBasic.map((column) => {
    if (column.key === "category") {
      return { ...column, label: t("category") };
    }

    if (column.key === "ongoingBudget") {
      return { ...column, label: t("ongoingBudget") };
    }

    if (column.key === "irregularBudget") {
      return { ...column, label: t("irregularBudget") };
    }

    return column;
  });
}

export function budgetGridHeaderGroupsBasic(t = (key) => key) {
  return [
    {
      key: "category",
      label: "",
      columns: ["category"],
      columnLabels: {
        category: t("category")
      }
    },
    {
      key: "ongoing",
      label: t("ongoing"),
      columns: ["ongoingBudget"],
      columnLabels: {
        ongoingBudget: t("budget")
      }
    },
    {
      key: "irregular",
      label: t("irregular"),
      columns: ["irregularBudget"],
      columnLabels: {
        irregularBudget: t("budget")
      }
    }
  ];
}

export function budgetGridHeaderGroups(t = (key) => key) {
  return [
    {
      key: "category",
      label: "",
      columns: ["category"],
      columnLabels: {
        category: t("category")
      }
    },
    {
      key: "ongoing",
      label: t("ongoing"),
      columns: ["ongoingBudget", "impliedOngoing", "ongoingDelta"],
      columnLabels: {
        ongoingBudget: t("budget"),
        impliedOngoing: t("implied"),
        ongoingDelta: t("variance")
      }
    },
    {
      key: "irregular",
      label: t("irregular"),
      columns: ["irregularBudget", "irregularSpent", "irregularDelta"],
      columnLabels: {
        irregularBudget: t("budget"),
        irregularSpent: t("spent"),
        irregularDelta: t("variance")
      }
    }
  ];
}
